import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import QrCodeScanner from '@mui/icons-material/QrCodeScanner';
import jsQR from 'jsqr';
import { normalizeCastCode } from '../../cast/YomiriP2PBridge.ts';

export function YomiriQrScannerModal({
  open,
  onClose,
  onScan,
}: {
  open: boolean;
  onClose: () => void;
  onScan: (code: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [cameraError, setCameraError] = useState<string>('');
  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      stopCamera();
      return;
    }

    setCameraError('');
    startCamera();

    return () => {
      stopCamera();
    };
  }, [open]);

  const stopCamera = () => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError(
          location.protocol !== 'https:' && location.hostname !== 'localhost'
            ? 'Los navegadores bloquean la cámara en redes HTTP sin SSL. Ingresá el código corto de 6 caracteres que muestra la tele abajo del QR.'
            : 'Tu navegador no permite acceso a la cámara.'
        );
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        await videoRef.current.play();
        scanFrame();
      }
    } catch (err: any) {
      setCameraError(
        err.name === 'NotAllowedError'
          ? 'Permiso de cámara denegado. Habilitá la cámara en tu navegador.'
          : 'No se pudo acceder a la cámara trasera.'
      );
    }
  };

  const scanFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animFrameRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      animFrameRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });

    if (code && code.data) {
      // Check if it's a direct cast URL or code: e.g. "http://.../#cast=ABC..." or raw code
      let castPin = '';
      if (code.data.includes('cast=')) {
        try {
          const url = new URL(code.data);
          const hashParam = new URLSearchParams(url.hash.replace(/^#/, '')).get('cast');
          const queryParam = url.searchParams.get('cast');
          castPin = hashParam || queryParam || '';
        } catch {
          const match = code.data.match(/cast=([A-Za-z0-9]+)/);
          if (match) castPin = match[1];
        }
      } else {
        castPin = code.data;
      }

      const normalized = normalizeCastCode(castPin);
      if (normalized.length >= 4) {
        stopCamera();
        onScan(normalized);
        onClose();
        return;
      }
    }

    animFrameRef.current = requestAnimationFrame(scanFrame);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'var(--y-containerLow, #141117)',
            border: '1px solid var(--y-outlineVariant, rgba(255, 255, 255, 0.08))',
            borderRadius: '20px',
            color: '#ffffff',
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeScanner sx={{ color: 'var(--y-primary, #ff375f)' }} />
          <Typography sx={{ fontSize: 16, fontWeight: 750 }}>Escanear QR de la TV</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--y-onSurfaceVariant)' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 13, color: 'var(--y-onSurfaceVariant, #ab9ea7)', mb: 2, textAlign: 'center' }}>
          Apuntá con la cámara al código QR en la pantalla de tu televisor.
        </Typography>

        {cameraError ? (
          <Box
            sx={{
              p: 3,
              borderRadius: '12px',
              bgcolor: 'rgba(255, 55, 95, 0.1)',
              border: '1px solid var(--y-error, #ff5470)',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: 13, color: 'var(--y-error, #ff5470)', fontWeight: 600 }}>
              {cameraError}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 320,
              aspectRatio: '1',
              borderRadius: '16px',
              overflow: 'hidden',
              bgcolor: '#000000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Viewfinder Target Guide */}
            <Box
              sx={{
                position: 'absolute',
                width: 200,
                height: 200,
                border: '2px solid var(--y-primary, #ff375f)',
                borderRadius: '16px',
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none',
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
